<!-- layouts/PrivateLayout.vue -->
<template>
  <!--
  <NavBar :nav-links="navLinks"/>
  -->
  <div class="layout-container">
    <Loading/>
    <!-- Mobile Open Menu Button -->

    <button v-if="isMobile && !isSidebarOpen" class="mobile-menu-btn fixed top-4 right-4" @click="openSidebar">
      <MenuIcon class="icon" />
    </button>
    <Sidebar
      v-if="!isMobile || isSidebarOpen"
      :nav-links="navLinks"
      :is-collapsed="isSidebarCollapsed"
      :is-mobile="isMobile"
      @toggle-sidebar="toggleSidebar"
    />
    <!-- This could be a sidebar for internal navigation -->
    <div class="main-container" :class="{ 'collapsed': isSidebarCollapsed, 'mobile': isMobile }">
      <!-- ✅ Main Content Area -->
      <main ref="mainContentContainer" class="content overflow-y-auto scroll-smooth">
        <div class="flex-grow">
          <slot /> <!-- Renders the page content here -->
        </div>
        <Footer />
        <Background2 />
        <BackToTop :scroll-target="mainContentContainer" />
        <DevDrawer v-if="devMode || isUserTypeAdmin" />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, onMounted, provide } from 'vue';
import Sidebar from "@/components/layout/Sidebar.vue"; // Assuming you have a Sidebar component
import Footer from "@/views/my/components/Footer.vue";
import type {NavBarLinkType} from "@/types/layout";
import BackToTop from "@/components/BackToTop.vue";
import Background2 from "@/components/Background2.vue";
import {
  Calendar,
  ClipboardList,
  GitPullRequest,
  Home,
  MonitorPlay,
  User,
  SquareUserRound,
  Rocket,
  Crown,
  MenuIcon,
  X as XIcon, Cross
} from "lucide-vue-next";
import {useAppComposable} from "@/composables/useAppComposable";
import DevDrawer from "@/views/my/dev/components/DevDrawer.vue";
import Loading from "@/components/layout/Loading.vue";
const devMode = import.meta.env.DEV; // returns true in development

const { isUserTypeAdmin } = useAppComposable();
const navLinks: NavBarLinkType[] = [
  { id: 'home', label: 'Home', route: '/my/home', icon: Home },
  { id: 'account', label: 'Account', route: '/my/account', icon: User },
  { id: 'profile', label: 'Profile', route: '/my/profile', icon: SquareUserRound },
  { id: 'events', label: 'Events', route: '/my/events', icon: Calendar },
  { id: 'applications', label: 'Applications', route: '/my/applications', icon: GitPullRequest },
  { id: 'assignments', label: 'Assignments', route: '/my/assignments', icon: ClipboardList },
  { id: 'viewer', label: 'Viewer', route: '/my/viewer', icon: MonitorPlay },
  { id: 'favorites', label: 'Favorites', route: '/my/favorites', icon: Crown },

  { id: 'help', label: 'Help Center', route: '/my/help-center', icon: Cross, divider: true },
  // 👇 Dev-only link
  ...(devMode ? [
    { id: 'devtools', label: 'Dev Tools', route: '/my/dev-tools', icon: Rocket }
  ] : [])
  /*
  { id: 'home', label: 'Dashboard', route: { url: '/my/home' } },
  { id: 'events', label: 'My Events', route: { url: '/my/events' } },
  { id: 'applications', label: 'Applications', route: { url: '/my/applications' } },
  { id: 'assignments', label: 'Assignments', route: { url: '/my/assignments' } },
  { id: 'viewer', label: 'Viewer', route: { url: '/my/viewer' } },

   */
];

const isMobile = ref(window.innerWidth < 768);
const isSidebarOpen = ref(!isMobile.value); // Initially open on desktop
const mainContentContainer = ref<HTMLElement | null>(null)
const isSidebarCollapsed = ref(false);

const checkScreenSize = () => {
  isMobile.value = window.innerWidth < 768;
  if (isMobile.value) {
    isSidebarOpen.value = false;
    isSidebarCollapsed.value = false; // on mobile we always want expanded sidebar
  } else {
    isSidebarOpen.value = true;
  }
};

// This listens to the event emitted from Sidebar.vue to update state
const toggleSidebar = () => {
  if (isMobile.value) {
    isSidebarOpen.value = !isSidebarOpen.value;
  } else {
    isSidebarCollapsed.value = !isSidebarCollapsed.value;
  }
};
// Provide the function, so that the view details can use it.
provide('toggleSidebar', toggleSidebar);

const openSidebar = () => {
  isSidebarOpen.value = true;
};
const closeSidebar = () => {
  isSidebarOpen.value = false;
};
onMounted(() => {
  checkScreenSize();
  window.addEventListener('resize', checkScreenSize);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenSize);
});
// CSS class for the main container
const mainContainerClass = computed(() => ({
  collapsed: isSidebarCollapsed.value,
  mobile: isMobile.value,
}));


</script>
<style scoped>
/* ✅ Use Flexbox for Proper Layout */
.layout-container {
  display: flex;
  /*flex-direction: column;*/
  height: 100vh;
}

/* ✅ Ensure Main Content & Sidebar Are Side by Side */
.main-container {
  margin-left: 250px;
  flex-grow: 1;
  transition: margin-left 0.3s ease-in-out;

  /* Navigation at the bottom */
  display: flex;
  flex-direction: column;
}

/* Adjusts based on sidebar state */
.main-container.collapsed {
  margin-left: 80px;  /* Adjust when sidebar is collapsed */
}



/* ✅ For Mobile */
.main-container.mobile {
  margin-left: 0px !important; /* override other rules */
}

/* Navigation at the bottom */
.content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.mobile-menu-btn {
  z-index: 1000;
  color: white;
}
</style>
